import { useCallback } from "react";
import { ItemType, Filter } from "../../../types";
import { AVAILABLE_FILTERS } from "../utils/filters";

type UseVideoItemSettingsProps = {
  selectedItemObject: ItemType | undefined;
  changeItem: (itemId: string, recipe: (item: ItemType) => ItemType) => void;
};

const generateFilterId = () => `filter_${Date.now()}_${Math.random()}`;

export const useVideoItemSettings = ({
  selectedItemObject,
  changeItem,
}: UseVideoItemSettingsProps) => {
  const setFilterValue = useCallback(
    (filterType: string, newValue: number) => {
      if (selectedItemObject?.type !== "video") return;

      const filterConfig = AVAILABLE_FILTERS.find(
        (f) => f.value === filterType
      );
      if (!filterConfig) return;

      changeItem(selectedItemObject.id, (item) => {
        if (item.type !== "video") {
          return item;
        }

        const existingFilters = item.configuration.filters ?? [];
        const existingFilter = existingFilters.find(
          (f) => f.type === filterType
        );
        const isDefaultValue = newValue === filterConfig.defaultValue;

        let newFilters: Filter[];

        if (isDefaultValue) {
          newFilters = existingFilters.filter((f) => f.type !== filterType);
        } else {
          if (existingFilter) {
            newFilters = existingFilters.map((f) =>
              f.type === filterType ? { ...f, value: newValue } : f
            );
          } else {
            const newFilterToAdd: Filter = {
              id: generateFilterId(),
              type: filterConfig.value,
              value: newValue,
              unit: filterConfig.unit,
            };
            newFilters = [...existingFilters, newFilterToAdd];
          }
        }

        return {
          ...item,
          configuration: {
            ...item.configuration,
            filters: newFilters,
          },
        };
      });
    },
    [selectedItemObject, changeItem]
  );

  return {
    setFilterValue,
  };
};
